import { WillData } from './schema';
import { toTitleCase } from './utils';
import _ from 'lodash';

type DeepPartial<T> =
    T extends (infer U)[]
    ? DeepPartial<U>[]
    : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

export interface WillDiff {
    section: string;
    changes: string[];
}

export const generateWillDiff = (original: DeepPartial<WillData>, current: DeepPartial<WillData>): WillDiff[] => {
    const diffs: WillDiff[] = [];

    // Helper to add changes
    const addChange = (section: string, change: string) => {
        let sectionDiff = diffs.find(d => d.section === section);
        if (!sectionDiff) {
            sectionDiff = { section, changes: [] };
            diffs.push(sectionDiff);
        }
        sectionDiff.changes.push(change);
    };

    // 1. Personal Details
    if (!_.isEqual(original.personalDetails, current.personalDetails)) {
        if (original.personalDetails?.fullName !== current.personalDetails?.fullName) {
            addChange('Personal Details', `Name changed from "${original.personalDetails?.fullName || ''}" to "${current.personalDetails?.fullName || ''}"`);
        }
        if (original.personalDetails?.maritalStatus !== current.personalDetails?.maritalStatus) {
            addChange('Personal Details', `Marital status updated`);
        }
        if (!_.isEqual(original.personalDetails?.address, current.personalDetails?.address)) {
            addChange('Personal Details', `Address updated`);
        }
        // Generic catch-all for other personal details
        if (diffs.find(d => d.section === 'Personal Details')?.changes.length === 0) {
            addChange('Personal Details', 'Details updated');
        }
    }

    // 2. Children
    const originalChildren = original.children || [];
    const currentChildren = current.children || [];
    if (!_.isEqual(originalChildren, currentChildren)) {
        if (originalChildren.length !== currentChildren.length) {
            addChange('Children', `Number of children changed from ${originalChildren.length} to ${currentChildren.length}`);
        } else {
            addChange('Children', 'Children details updated');
        }
    }

    // 3. Guardians
    const originalGuardians = original.guardians || [];
    const currentGuardians = current.guardians || [];
    if (!_.isEqual(originalGuardians, currentGuardians)) {
        if (originalGuardians.length !== currentGuardians.length) {
            addChange('Guardians', `Number of guardians changed from ${originalGuardians.length} to ${currentGuardians.length}`);
        } else {
            addChange('Guardians', 'Guardian details updated');
        }
    }

    // 4. Beneficiaries
    const originalBeneficiaries = original.beneficiaries || [];
    const currentBeneficiaries = current.beneficiaries || [];
    if (!_.isEqual(originalBeneficiaries, currentBeneficiaries)) {
        if (originalBeneficiaries.length !== currentBeneficiaries.length) {
            addChange('Beneficiaries', `Number of beneficiaries changed from ${originalBeneficiaries.length} to ${currentBeneficiaries.length}`);
        } else {
            // Check for percentage changes
            const percentageChanged = originalBeneficiaries.some((b, i) => b?.percentage !== currentBeneficiaries[i]?.percentage);
            if (percentageChanged) {
                addChange('Beneficiaries', 'Beneficiary shares updated');
            } else {
                addChange('Beneficiaries', 'Beneficiary details updated');
            }
        }
    }

    // 5. Executors
    const originalExecutors = original.executors || [];
    const currentExecutors = current.executors || [];
    if (!_.isEqual(originalExecutors, currentExecutors)) {
        if (originalExecutors.length !== currentExecutors.length) {
            addChange('Executors', `Number of executors changed from ${originalExecutors.length} to ${currentExecutors.length}`);
        } else {
            addChange('Executors', 'Executor details updated');
        }
    }

    // 6. Gifts
    const originalGifts = original.gifts || [];
    const currentGifts = current.gifts || [];
    if (!_.isEqual(originalGifts, currentGifts)) {
        if (originalGifts.length !== currentGifts.length) {
            addChange('Gifts', `Number of gifts changed from ${originalGifts.length} to ${currentGifts.length}`);
        } else {
            addChange('Gifts', 'Gift details updated');
        }
    }

    // 7. Funeral Wishes
    if (original.funeralWishes !== current.funeralWishes) {
        addChange('Funeral Wishes', 'Funeral wishes updated');
    }

    // 8. Exclusions (note and list)
    const originalExclusions = original.exclusions || [];
    const currentExclusions = current.exclusions || [];
    if (!_.isEqual(originalExclusions, currentExclusions)) {
        if (originalExclusions.length !== currentExclusions.length) {
            addChange('Exclusions', `Number of exclusions changed from ${originalExclusions.length} to ${currentExclusions.length}`);
        } else {
            addChange('Exclusions', 'Exclusion entries updated');
        }
    }

    if (original.excludeChoice !== current.excludeChoice || original.exclusionMessage !== current.exclusionMessage) {
        addChange('Exclusions', 'Exclusion note updated');
    }

    // 9. Additional instructions
    if ((original.additionalInstructions || '') !== (current.additionalInstructions || '')) {
        addChange('Additional Instructions', 'Additional instructions updated');
    }

    // 10. Disaster fallback
    if (!_.isEqual(original.disasterFallback, current.disasterFallback)) {
        addChange('Disaster Fallback', 'Disaster fallback preferences updated');
    }

    return diffs;
};
